// CFG.I18N.A1: Wizard language picker.
//
// Renders a `<select>` of locales sourced from `AVAILABLE_LOCALES` in
// i18n.ts. While the array has length 1 (English-only sprint) the
// component renders nothing — by design, so the wizard footers can mount
// it unconditionally and the dropdown only appears once a second locale
// is added in a future sprint. No code change needed in the wizards
// when that happens.
//
// Why a native <select> instead of a custom dropdown: native selects get
// keyboard navigation, screen-reader labelling, and mobile-OS picker UI
// for free. The wizard footer position doesn't need a fancy popover.

import { useTranslation } from 'react-i18next';
import { AVAILABLE_LOCALES } from '../i18n';

export interface LanguageSwitcherProps {
  /** Optional className passthrough for footer/header alignment. */
  className?: string;
  /** Optional inline style passthrough. */
  style?: React.CSSProperties;
}

export default function LanguageSwitcher({ className, style }: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();

  // Hide the picker entirely while only one locale ships. Using `null`
  // (not `display: none`) keeps the DOM clean for a11y tools and means
  // the surrounding flex/grid layouts don't collapse around an invisible
  // node.
  if (AVAILABLE_LOCALES.length <= 1) return null;

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    void i18n.changeLanguage(event.target.value);
  };

  return (
    <select
      aria-label={t('languageSwitcher.ariaLabel')}
      data-testid="language-switcher"
      value={i18n.resolvedLanguage ?? i18n.language}
      onChange={handleChange}
      className={className}
      style={{
        height: 36,
        padding: '0 10px',
        background: 'var(--surface-alt)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        fontFamily: 'var(--font-body)',
        fontSize: 13,
        color: 'var(--text)',
        cursor: 'pointer',
        ...style,
      }}
    >
      {AVAILABLE_LOCALES.map((locale) => (
        <option key={locale.code} value={locale.code}>
          {locale.label}
        </option>
      ))}
    </select>
  );
}
